import {SalesOrderModel} from "../../models/salesOrder";
import React, {useState} from "react";
import {useAppDispatch} from "../../app/hooks";
import Skeleton from "react-loading-skeleton";
import {ImageUtils} from "../../utils/imageUtils";
import {Image} from "../images/Image";
import {Body, Heading2, Heading3, Heading4, Paragraph} from "../text";
import {Button} from "../buttons/Button";
import {useTranslation} from "next-i18next";
import {useRouter} from "next/router";
import {CurrencyUtils} from "../../utils/currencyUtils";
import moment from "moment";
import {useModalRouting} from "../../utils/useModalRouting";
import {reset} from "../../redux/checkout/checkoutSlice";
import {useDeeplinkRouter} from "../../utils/useDeeplinkRouter";

export interface OrderListItemViewProps {
  order: SalesOrderModel
  isLoading: boolean
}

export const OrderListItemView: React.FC<OrderListItemViewProps> = ({ order, isLoading }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useRouter()
  const [isReordering, setIsReordering] = useState(false)
  const deeplinkRouter = useDeeplinkRouter()
  const imageUrl = ImageUtils.createUrlFromImage(order.vendor?.coverImage)
  const { createRoute } = useModalRouting();

  const price = CurrencyUtils.stringFromPrice(order.paymentSummary?.getTotal())
  const orderDate = moment(order.createdAt).format('MMM D, YYYY')

  return <div
    className={`flex w-full flex-col md:flex-row items-start space-y-4 md:space-y-0 md:space-x-4 lg:space-x-6 py-5 border-b last:border-0 border-fg-gray-surface`}
  >
    {<div className="flex-shrink-0 rounded-lg w-[calc(100vw-2rem)] sm:w-[calc(100vw-4rem)] md:h-16 md:w-24 lg:h-32 lg:w-48">
      {isLoading && <Skeleton className={`w-full h-full !aspect-h-1 !aspect-w-3 !md:h-16 !md:w-24 !lg:h-32 !lg:w-48 !rounded-lg`}/>}
      {!isLoading && imageUrl && <Image
          src={imageUrl}
          alt={order.vendor?.name}
          layout="fill"
          objectFit="cover"
          sizeClass="w-full aspect-h-1 aspect-w-3 md:h-16 md:w-24 lg:h-32 lg:w-48"
          className="rounded-lg bg-fg-gray-surface"
      />}
    </div>}

    <div className="flex flex-col space-y-4 flex-grow">
      <Heading2 className="text-left">
        {isLoading ? <Skeleton className="!w-48 !lg:w-1/2" /> : order.vendor?.name}
      </Heading2>

      <div>
        {isLoading ? <Skeleton className="!w-32" /> : <Body>{price} • {orderDate}</Body>}
      </div>

      <div className="flex flex-col space-y-2">
        {order.lineItems.map(item => {
          const optionDescription = item.optionValues
            .filter(o => o.value !== 'Default Title')
            .map(o => o.value)
            .join(', ')

          return <div key={item.id} className="flex space-x-3 items-center">
            {isLoading ? <Skeleton className="!w-10 !h-10 !rounded-full !flex-shrink-0" /> : <div className="bg-fg-gray-surface flex justify-center items-center rounded-full h-10 w-10 flex-shrink-0">
              <Heading3>{item.quantity}x</Heading3>
            </div>}

            <div className="flex flex-col space-y-1 flex-grow">
              <Body>{isLoading ? <Skeleton className="!w-48 !lg:w-2/5" /> : item.name}</Body>
              {optionDescription && <Paragraph className={`text-fg-gray-dark`}>
                {isLoading ? <Skeleton className="!w-1/3"/> : optionDescription}
              </Paragraph>}
            </div>
          </div>
        })}
      </div>
    </div>

    <div className="w-full md:w-1/4 md:max-w-20 flex flex-col space-y-3 flex-shrink-0">
      <Button
        isFullWidth={true}
        buttonStyle="primary"
        isLoading={isReordering}
        isSkeletonLoading={isLoading}
        onClick={async () => {
          setIsReordering(true)
          const storeId = order.storeId ?? ''
          const items = order.lineItems.map(item => {
            return `${item.id}|${item.quantity}`
          }).join(',')

          const url = new URL('fitgenie://cart')
          url.searchParams.append('menu_items', items)
          url.searchParams.append('store_id', storeId)

          await deeplinkRouter.handleDeeplinkUrl(url)

          setIsReordering(false)
        }}
      >
        {t('order:order_title_reorder')}
      </Button>

      <Button
        isFullWidth={true}
        buttonStyle="secondary"
        isSkeletonLoading={isLoading}
        onClick={() => {
          router.push(
            createRoute({ name: 'order_detail', query: { order_id: order.id }  }),
            { pathname: '/order-detail', query: { order_id: order.id } },
            { shallow: true }
          );
        }}
      >
        {t('store:store_title_view_receipt')}
      </Button>
    </div>
  </div>
}